import type { InvestmentsReportTemplate } from "@eu.mdotm.api/sphere";
import { typedUrlForRoute, useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import ReactQueryWrapper from "$root/components/ReactQueryWrapper";
import { spawnDeleteDialog } from "$root/components/spawnable/entity-management/delete-dialog";
import { spawnDuplicateDialog } from "$root/components/spawnable/entity-management/duplicate-dialog";
import { spawnRenameDialog } from "$root/components/spawnable/entity-management/rename-dialog";
import {
	type DefaultReportTemplate,
	defaultPortfolioTemplates,
	defaultTemplateName,
	isDefaultReportTemplate,
	startingTemplate,
} from "$root/functional-areas/reports/default-templates";
import { reportRepository } from "$root/functional-areas/reports/repository";
import { platformToast } from "$root/notification-system/toast";
import { trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { actionsColumn } from "$root/ui-lib/interactive-collections/common-table-actions";
import { builtInCaseInsensitiveSortFor, parallelize, qualifier } from "$root/utils";
import type { Abortable, MaybePromise } from "@mdotm/utils/promise";
import {
	type OrderBy,
	type TableColumn,
	ActionText,
	Badge,
	BaseHScrollTable,
	Controller,
	DebouncedSearchInput,
	Icon,
	IconTooltip,
	TableDataCell,
	TableHeadCell,
	sortRows,
	useSelectableTableColumn,
} from "@mdotm/shadcn/legacy/components";
import { useSearchable } from "@mdotm/shadcn/legacy/headless";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { builtInSortFnFor, noop } from "@mdotm/shadcn/legacy/utils";
import { useMemo, useState } from "react";
import { BulkActions, Button, IconButton } from "@mdotm/shadcn/mdotm/components";

const ReportCustomisation = ({ active }: { active: boolean }): React.JSX.Element => {
	const { push } = useTypedNavigation();

	async function shadowCreation(name: string) {
		try {
			const sphereTemplate = defaultPortfolioTemplates.find((x) => x.id === "sphere");

			const created = await reportRepository.create({
				templateName: name,
				visible: false,
				format: "pdf",
				data: {
					...(name === sphereTemplate?.templateName ? sphereTemplate?.data : startingTemplate?.data),
					status: "draft",
				},
			});

			trackMixPanelEvent("reportBuilder.entityActions.create", {
				report: { uuid: created.uuid!, name: created.templateName! },
			});
			push("PortfolioStudioSettings/ReportEditor", { reportUid: created.uuid! });
		} catch (error) {
			platformToast({
				children: "Oops, Something went wrong, pleas re-try",
				icon: "pdf",
				severity: "error",
			});
			throw error;
		}
	}

	return (
		<ReactQueryWrapper {...reportRepository.templates.getQueryConfig()} enabled={active}>
			{(data, query) => (
				<ReportCustomisationInner
					rows={data}
					onDelete={async (uuid) => {
						await reportRepository.delete(uuid);
						await query.refetch();
					}}
					checkIfNameIsAvailable={reportRepository.isReportNameAvailable}
					onDuplicate={async (uuid, name) => {
						const created = await reportRepository.create({
							...data.find((x) => !isDefaultReportTemplate(x) && x.uuid === uuid)!,
							templateName: name,
						});
						trackMixPanelEvent("reportBuilder.entityActions.create", {
							report: { uuid: created.uuid!, name: created.templateName! },
						});
						await query.refetch();
					}}
					onRename={async (uuid, newName) => {
						await reportRepository.update(uuid, {
							...data.find((x) => !isDefaultReportTemplate(x) && x.uuid === uuid)!,
							templateName: newName,
						});
						await query.refetch();
					}}
					onCreate={async () => {
						const defaultTemplates = data.filter((x) => x.templateName?.includes(defaultTemplateName));
						const findMax = defaultTemplates.reduce((acc, el) => {
							const [_name, version] = el.templateName?.split(defaultTemplateName) ?? [];
							if (version && version !== "" && !isNaN(Number(version))) {
								acc = Math.max(Number(version), acc);
							}
							return acc;
						}, 0);
						await shadowCreation(
							defaultTemplates.length > 0 ? `${defaultTemplateName} ${findMax + 1}` : defaultTemplateName,
						);
					}}
					toggleVisibility={async (row) => {
						if (isDefaultReportTemplate(row)) {
							await reportRepository.create({
								templateName: row.templateName,
								visible: false,
								format: "pdf",
								data: row.data,
							});
						} else {
							if (!row.uuid) {
								throw new Error("missing template uuid");
							}
							await reportRepository.update(row.uuid, {
								...row,
								visible: !row.visible,
							});
							await query.refetch();
						}
					}}
					onBulkDelete={async (uuids) => {
						let successCounter = 0;
						await parallelize(uuids.map((uuid) => () => reportRepository.delete(uuid).then(() => successCounter++)));
						platformToast({
							children: `You have successfully deleted “${successCounter}” templates`,
							icon: "pdf",
							severity: "success",
						});
						await query.refetch();
					}}
				/>
			)}
		</ReactQueryWrapper>
	);
};

function ReportCustomisationInner(props: {
	rows: Array<InvestmentsReportTemplate | DefaultReportTemplate>;
	toggleVisibility: (row: InvestmentsReportTemplate | DefaultReportTemplate) => MaybePromise<void>;
	onCreate: () => MaybePromise<void>;
	onDelete: (uuid: string) => MaybePromise<void>;
	checkIfNameIsAvailable: (name: string, opts?: Abortable) => MaybePromise<boolean>;
	onRename: (uuid: string, newName: string) => MaybePromise<void>;
	onDuplicate: (uuid: string, name: string) => MaybePromise<void>;
	onBulkDelete: (uuids: string[]) => Promise<void>;
}) {
	const [query, setQuery] = useState("");

	const rows = useMemo(
		() =>
			props.rows.reduce<{
				default: Array<InvestmentsReportTemplate | DefaultReportTemplate>;
				custom: Array<InvestmentsReportTemplate | DefaultReportTemplate>;
			}>(
				(acc, report) => {
					if (isDefaultReportTemplate(report) || report.templateName === defaultPortfolioTemplates[0].templateName) {
						acc.default.push(report);
					} else {
						acc.custom.push(report);
					}

					return acc;
				},
				{ default: [], custom: [] },
			),
		[props.rows],
	);

	const { filtered } = useSearchable({
		collection: query ? props.rows : rows.custom,
		query,
		extractFn: (row) => row.templateName ?? "",
	});

	const checkboxColumnData = useSelectableTableColumn({
		rows: props.rows,
		selectBy: (r) => (!isDefaultReportTemplate(r) ? (r.uuid ?? "") : ""),
		selectableRowIds: props.rows.flatMap((r) =>
			isDefaultReportTemplate(r) || r.templateName === defaultPortfolioTemplates[0].templateName ? [] : [r.uuid ?? ""],
		),
		filteredRows: filtered,
	});

	const { toggleVisibility, onDelete, onRename, onDuplicate, checkIfNameIsAvailable } = props;
	const isSpereDefaultTemplateSelected = useMemo(
		() =>
			checkboxColumnData.multiSelectCtx.selection
				.toArray()
				.some((x) =>
					props.rows.find((row) =>
						isDefaultReportTemplate(row)
							? false
							: row.uuid === x && row.templateName === defaultPortfolioTemplates[0].templateName,
					),
				),
		[checkboxColumnData.multiSelectCtx.selection, props.rows],
	);

	const columns = useMemo<Array<TableColumn<InvestmentsReportTemplate | DefaultReportTemplate, string>>>(
		() => [
			checkboxColumnData.column,
			{
				name: "name",
				header: "Name",
				content: (row, cellProps) => (
					<TableDataCell {...cellProps}>
						<div className="flex space-x-2 items-center">
							<ActionText
								href={typedUrlForRoute("PortfolioStudioSettings/ReportEditor", {
									reportUid: isDefaultReportTemplate(row) ? row.id : row.uuid!,
								})}
								data-qualifier={qualifier.reportCustomisation.studio.templateLink(row.templateName)}
							>
								{row.templateName}
							</ActionText>
							{(isDefaultReportTemplate(row) || row.templateName === defaultPortfolioTemplates[0].templateName) && (
								<Badge size="x-small" backgroundColor={themeCSSVars.palette_N200} color={themeCSSVars.palette_N800}>
									Default
								</Badge>
							)}
						</div>
					</TableDataCell>
				),
				width: 560,
				sortFn: builtInCaseInsensitiveSortFor("templateName"),
			},
			{
				name: "format",
				header: "Format",
				content: (row) => row.format,
				width: 192,
				sortFn: builtInCaseInsensitiveSortFor("format"),
			},
			{
				name: "visibility",
				sortFn: builtInSortFnFor("visible"),
				header: (headerProps) => (
					<TableHeadCell {...headerProps}>
						<span className="inline-flex items-center gap-2">
							<span className="line-clamp-2">Available on menu</span>
							<IconTooltip severity="info" overrideColor={themeCSSVars.palette_N300} iconSize={14}>
								You can choose to make this template visible in the download area for the portfolio list and portfolio
								detail page
							</IconTooltip>
						</span>
					</TableHeadCell>
				),
				content: (row, contentProps) => (
					<TableDataCell {...contentProps} onClick={(e) => e.stopPropagation()}>
						<IconButton
							size={20}
							color={themeCSSVars.palette_N400}
							icon={row.visible ? "show" : "Hide"}
							disabled={isDefaultReportTemplate(row)}
							onClickAsync={() => toggleVisibility(row)}
							data-qualifier={qualifier.reportCustomisation.studio.toggleVisibility}
						/>
					</TableDataCell>
				),
				width: 192,
			},
			actionsColumn({
				hideHeader: true,
				onSettingsClick: noop,
				dropdownActions: (row) =>
					isDefaultReportTemplate(row) || row.templateName === defaultPortfolioTemplates[0].templateName
						? []
						: [
								{
									onClick: () => {
										spawnDeleteDialog({
											entityType: "template",
											entityName: row.templateName ?? "",
											onDeleteAsync: () => onDelete(row.uuid!),
										}).catch(noop);
									},
									icon: "Delete",
									"data-qualifier": qualifier.reportCustomisation.studio.delete,
									label: "Delete",
								},
								{
									onClick: () => {
										spawnRenameDialog({
											entityType: "template",
											onSubmitAsync: (name) => onRename(row.uuid!, name),
											checkIfNameIsAvailable,
											currentName: row.templateName!,
											placeholder: "Template 123",
										}).catch(noop);
									},
									icon: "Edit",
									"data-qualifier": qualifier.reportCustomisation.studio.rename,
									label: "Rename",
								},
								{
									onClick: () => {
										spawnDuplicateDialog({
											entityType: "template",
											onSubmitAsync: (name) => onDuplicate(row.uuid!, name),
											checkIfNameIsAvailable,
											originalName: row.templateName!,
											placeholder: "Template 123",
										}).catch(noop);
									},
									icon: "Content-Copy",
									"data-qualifier": qualifier.reportCustomisation.studio.duplicate,
									label: "Duplicate",
								},
							],
				triggerProps: {
					"data-qualifier": qualifier.reportCustomisation.studio.moreActions,
				},
			}),
		],
		[checkboxColumnData.column, toggleVisibility, onDelete, checkIfNameIsAvailable, onRename, onDuplicate],
	);

	return (
		<>
			<div className="flex flex-row justify-between">
				<div className="max-w-md grow">
					<DebouncedSearchInput
						externalQuery={query}
						onChange={setQuery}
						placeholder="Filter by name"
						data-qualifier={qualifier.reportCustomisation.studio.search}
					/>
				</div>
				<div>
					<Button
						onClickAsync={() => props.onCreate()}
						palette="secondary"
						size="small"
						data-qualifier={qualifier.reportCustomisation.studio.newTemplate}
					>
						<Icon icon="Outline1" size={16} />
						&nbsp;New template
					</Button>
				</div>
			</div>
			<BulkActions
				variant="floating"
				count={checkboxColumnData.multiSelectCtx.selection.size}
				onClear={() => checkboxColumnData.multiSelectCtx.reset()}
				actions={[
					{
						label: "Delete",
						icon: "Delete",
						"data-qualifier": qualifier.reportCustomisation.studio.bulkDelete,
						...(isSpereDefaultTemplateSelected
							? {
									onClick: noop,
									disabled: true,
									tooltip: {
										children: "Sphere template cannot be deleted",
										overrideColor: themeCSSVars.palette_N300,
									},
								}
							: {
									disabled: checkboxColumnData.multiSelectCtx.selection.size === 0,
									onClick: () => {
										spawnDeleteDialog({
											entityNames: checkboxColumnData.multiSelectCtx.selection
												.toArray()
												.map(
													(id) =>
														props.rows.find((r) => !isDefaultReportTemplate(r) && r.uuid === id)?.templateName ?? "",
												),
											onDeleteAsync: async () => {
												await props.onBulkDelete(checkboxColumnData.multiSelectCtx.selection.toArray());
												checkboxColumnData.multiSelectCtx.reset();
											},
										}).catch(noop);
									},
								}),
					},
				]}
				className="my-2"
			/>
			<Controller value={defaultReportOrderBy}>
				{({ value: orderBy, onChange: onOrderByChange }) => (
					<BaseHScrollTable
						palette="uniform"
						columns={columns}
						rows={[
							...(query ? [] : rows.default),
							...(query ? filtered : sortRows({ rows: filtered, columns, orderByArr: orderBy })),
						]}
						orderBy={query ? undefined : orderBy}
						onOrderByChange={onOrderByChange}
						pinnedColumns={[
							{ name: "name", side: "left" },
							{ name: "settings-action", side: "right" },
						]}
						onRowClick={(row) => (!isDefaultReportTemplate(row) ? checkboxColumnData.toggle(row.uuid ?? "") : noop)}
						rowClassName={checkboxColumnData.rowClassName}
					/>
				)}
			</Controller>
		</>
	);
}

const defaultReportOrderBy: Array<OrderBy<"name">> = [{ columnName: "name", direction: "asc" }];

export default ReportCustomisation;
